import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react'
import toast from 'react-hot-toast'
import { scalekitAuthService, ScalekitUser, LoginOptions } from '../lib/scalekit.service'
import { organizationService, Organization, type OnboardingProgress } from '../lib/organization.service'
import { apiClient } from '../lib/api-client'
import { startRealtimeAuth, stopRealtimeAuth } from '../lib/realtimeAuth'

export interface AuthUser {
  id: string
  email: string | null
  full_name: string | null
  avatar_url?: string
  organization_id: string | null
}

interface PasswordlessState {
  authRequestId: string | null
  email: string | null
  expiresIn: number | null
}

interface AuthContextType {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
  // Organization state
  currentOrganization: Organization | null
  organizations: Organization[]
  needsOnboarding: boolean
  isCheckingOrgs: boolean
  // Onboarding progress
  onboardingProgress: OnboardingProgress | null
  // Passwordless state
  passwordlessState: PasswordlessState | null
  // Methods
  signIn: (options?: LoginOptions) => Promise<void>
  signInWithEmail: (email: string) => Promise<void>
  verifyCode: (code: string) => Promise<void>
  signOut: () => Promise<void>
  refreshSession: () => Promise<boolean>
  clearError: () => void
  clearPasswordlessState: () => void
  checkOnboardingStatus: () => Promise<boolean>
  setCurrentOrganization: (org: Organization) => void
  /** Directly update auth state after org creation — no second API call needed. */
  onOrgCreated: (org: Organization) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: React.ReactNode
}

function mapScalekitUserToAuthUser(user: ScalekitUser): AuthUser {
  return {
    id: user.user_id,
    email: user.email,
    full_name: user.name,
    organization_id: user.organization_id,
  }
}

export const AuthProvider: React.FC<AuthProviderProps> = React.memo(({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Organization state
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [needsOnboarding, setNeedsOnboarding] = useState(false)
  const [isCheckingOrgs, setIsCheckingOrgs] = useState(false)

  // Onboarding progress (fetched per org)
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingProgress | null>(null)

  // Passwordless state
  const [passwordlessState, setPasswordlessState] = useState<PasswordlessState | null>(null)

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const handleError = useCallback((error: unknown, defaultMessage: string) => {
    console.error('Auth error:', error)
    const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : ''

    // Map known Scalekit/gRPC error codes to user-friendly messages
    if (raw.includes('AUTH_REQUEST_EXPIRED')) {
      setError('Your verification code has expired. Please request a new one.')
    } else if (raw.includes('INVALID_CODE') || raw.includes('invalid code')) {
      setError('Invalid code. Please check and try again.')
    } else if (raw.includes('TOO_MANY_ATTEMPTS') || raw.includes('rate limit')) {
      setError('Too many attempts. Please wait a moment before trying again.')
    } else if (raw.includes('INVALID_ARGUMENT') && !raw.includes('AUTH_REQUEST_EXPIRED')) {
      setError('Something went wrong. Please try again.')
    } else if (raw && !raw.includes('GRPC:') && !raw.includes('error_code:') && raw.length < 200) {
      // Only show raw message if it's short and doesn't contain internal details
      setError(raw)
    } else {
      setError(defaultMessage)
    }
  }, [])

  // Check if user needs onboarding (no organization membership)
  const checkOnboardingStatus = useCallback(async (): Promise<boolean> => {
    try {
      setIsCheckingOrgs(true)
      const response = await organizationService.getMyOrganizations()

      // Only update onboarding state when the API actually responded.
      // On transient errors (is_authoritative=false), preserve previous state
      // so we don't randomly redirect an existing user to /onboarding.
      if (!response.is_authoritative) {
        console.warn('Org check returned non-authoritative result, keeping current state')
        return needsOnboarding
      }

      setOrganizations(response.organizations)
      setNeedsOnboarding(response.needs_onboarding)

      // Set current organization to first one if available
      if (response.organizations.length > 0 && !currentOrganization) {
        setCurrentOrganization(response.organizations[0])
        // Sync tenant ID to apiClient for Control API requests
        apiClient.setTenantId(response.organizations[0].id)
      }

      return response.needs_onboarding
    } catch (err) {
      console.warn('Failed to check organization status:', err)
      // Preserve existing state — don't redirect on transient failure
      return needsOnboarding
    } finally {
      setIsCheckingOrgs(false)
    }
  }, [currentOrganization, needsOnboarding])

  // Initialize auth state on app start
  useEffect(() => {
    let sessionCheckInterval: NodeJS.Timeout | undefined

    const initializeAuth = async () => {
      try {
        setIsLoading(true)

        // Rehydrate org from sessionStorage for instant display while we validate
        const cachedOrg = sessionStorage.getItem('sb_current_org')
        if (cachedOrg) {
          try {
            const org = JSON.parse(cachedOrg) as Organization
            setCurrentOrganization(org)
            apiClient.setTenantId(org.id)
          } catch { /* corrupt cache, ignore */ }
        }

        // Fire auth check and org fetch in parallel — both use the same
        // httpOnly session cookie, neither depends on the other's result.
        const [userResult, orgResult] = await Promise.allSettled([
          scalekitAuthService.getCurrentUser(),
          organizationService.getMyOrganizations(),
        ])

        let currentUser = userResult.status === 'fulfilled' ? userResult.value : null
        let orgResponse = orgResult.status === 'fulfilled' ? orgResult.value : null

        // Access cookie may have expired (1h TTL) while refresh cookie is still
        // valid (30 days). Try a refresh before declaring the user logged out —
        // otherwise users would land on /login at every cold start past the hour.
        if (!currentUser) {
          const refreshed = await scalekitAuthService.refreshSession()
          if (refreshed) {
            currentUser = await scalekitAuthService.getCurrentUser()
            if (currentUser) {
              // Re-fetch orgs since the previous parallel call also hit 401.
              orgResponse = await organizationService.getMyOrganizations().catch(() => null)
            }
          }
        }

        if (currentUser) {
          setUser(mapScalekitUserToAuthUser(currentUser))

          if (orgResponse?.is_authoritative) {
            // API gave us a definitive answer
            setOrganizations(orgResponse.organizations)
            setNeedsOnboarding(orgResponse.needs_onboarding)
            if (orgResponse.organizations.length > 0) {
              setCurrentOrganization(orgResponse.organizations[0])
              apiClient.setTenantId(orgResponse.organizations[0].id)
              // Cache for instant rehydration on next load
              sessionStorage.setItem('sb_current_org', JSON.stringify(orgResponse.organizations[0]))
              // Fetch onboarding progress for the org
              organizationService.getOnboardingProgress(orgResponse.organizations[0].id)
                .then(setOnboardingProgress).catch(() => {})
            }
          } else {
            // Org API failed or returned non-authoritative — trust cache if available
            console.warn('Org API unavailable during init, falling back to cached state')
            if (cachedOrg) {
              // cachedOrg was already restored to state above; keep needsOnboarding = false (default)
              console.info('Using cached organization while API is unavailable')
              toast('Using cached data — server temporarily unreachable', { icon: '⏳' })
            } else {
              // No cache AND API failed — genuinely unknown state.
              // Schedule a retry instead of assuming onboarding.
              console.warn('No cached org and API unavailable — will retry')
              setTimeout(async () => {
                const retry = await organizationService.getMyOrganizations()
                if (retry.is_authoritative) {
                  setOrganizations(retry.organizations)
                  setNeedsOnboarding(retry.needs_onboarding)
                  if (retry.organizations.length > 0) {
                    setCurrentOrganization(retry.organizations[0])
                    apiClient.setTenantId(retry.organizations[0].id)
                    sessionStorage.setItem('sb_current_org', JSON.stringify(retry.organizations[0]))
                  }
                }
              }, 2000)
            }
          }
        } else {
          // Not authenticated — clear any cached org
          sessionStorage.removeItem('sb_current_org')
        }

        // Set up periodic session validation (every 5 minutes).
        // Policy: never clear user state from this check. getCurrentUser()
        // returns null on ANY error (network, CORS, 5xx) — not just on
        // expired sessions — so a transient blip would otherwise log the
        // user out for nothing. A truly dead session will surface as 401s
        // on subsequent apiClient requests; signOut is user-initiated.
        sessionCheckInterval = setInterval(async () => {
          let sessionUser = await scalekitAuthService.getCurrentUser()
          if (!sessionUser) {
            const refreshed = await scalekitAuthService.refreshSession()
            if (refreshed) {
              sessionUser = await scalekitAuthService.getCurrentUser()
            }
            if (!sessionUser) {
              console.warn('periodic_session_check_inconclusive (preserving state)')
              return
            }
          }
          // Session confirmed alive — re-validate org membership (catches
          // server-side org deletion or membership revocation without a reload).
          const orgRefresh = await organizationService.getMyOrganizations()
          if (orgRefresh.is_authoritative) {
            setOrganizations(orgRefresh.organizations)
            setNeedsOnboarding(orgRefresh.needs_onboarding)
            if (orgRefresh.organizations.length > 0) {
              setCurrentOrganization(orgRefresh.organizations[0])
              apiClient.setTenantId(orgRefresh.organizations[0].id)
              sessionStorage.setItem('sb_current_org', JSON.stringify(orgRefresh.organizations[0]))
            }
          }
        }, 5 * 60 * 1000) // 5 minutes
      } catch (error) {
        handleError(error, 'Failed to initialize authentication')
        setUser(null)
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()

    return () => {
      if (sessionCheckInterval) {
        clearInterval(sessionCheckInterval)
      }
    }
  }, [handleError])

  const signIn = useCallback(async (options: LoginOptions = {}) => {
    try {
      setIsLoading(true)
      setError(null)

      // Scalekit handles all auth methods via hosted login page
      await scalekitAuthService.initiateLogin(options)
      // Browser will redirect, so we don't reach here
    } catch (error) {
      handleError(error, 'Sign in failed. Please try again.')
      setIsLoading(false)
      throw error
    }
  }, [handleError])

  const signInWithEmail = useCallback(async (email: string) => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await scalekitAuthService.sendPasswordlessCode(email)

      setPasswordlessState({
        authRequestId: response.auth_request_id,
        email: email,
        expiresIn: response.expires_in,
      })
    } catch (error) {
      handleError(error, 'Failed to send verification code.')
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [handleError])

  const verifyCode = useCallback(async (code: string) => {
    if (!passwordlessState?.authRequestId) {
      throw new Error('No pending verification. Please request a new code.')
    }

    try {
      setIsLoading(true)
      setError(null)

      const response = await scalekitAuthService.verifyPasswordlessCode(
        passwordlessState.authRequestId,
        code
      )

      // Passwordless verify returns email (not user_id) on success
      if (response.success && (response.email || response.user_id)) {
        // Authentication successful - fetch user info from /auth/me
        const currentUser = await scalekitAuthService.getCurrentUser()
        if (currentUser) {
          setUser(mapScalekitUserToAuthUser(currentUser))

          // Check organization status
          try {
            const orgResponse = await organizationService.getMyOrganizations()
            if (orgResponse.is_authoritative) {
              setOrganizations(orgResponse.organizations)
              setNeedsOnboarding(orgResponse.needs_onboarding)
              if (orgResponse.organizations.length > 0) {
                setCurrentOrganization(orgResponse.organizations[0])
                // Sync tenant ID to apiClient for Control API requests
                apiClient.setTenantId(orgResponse.organizations[0].id)
              }
            } else {
              console.warn('Org API unavailable after login, will retry')
              // Don't set needsOnboarding — schedule retry
              setTimeout(async () => {
                const retry = await organizationService.getMyOrganizations()
                if (retry.is_authoritative) {
                  setOrganizations(retry.organizations)
                  setNeedsOnboarding(retry.needs_onboarding)
                  if (retry.organizations.length > 0) {
                    setCurrentOrganization(retry.organizations[0])
                    apiClient.setTenantId(retry.organizations[0].id)
                  }
                }
              }, 2000)
            }
          } catch (orgErr) {
            console.warn('Failed to fetch organizations:', orgErr)
            // Preserve current state — don't redirect on transient failure
          }
        }

        setPasswordlessState(null)
      }
    } catch (error) {
      handleError(error, 'Invalid verification code.')
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [passwordlessState, handleError])

  const clearPasswordlessState = useCallback(() => {
    setPasswordlessState(null)
  }, [])

  const signOut = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      // Call backend to clear session cookies (don't redirect to Scalekit - skip their ugly logout page)
      await scalekitAuthService.logout(false)

      // Clear React state and redirect to login
      setUser(null)
      setCurrentOrganization(null)
      setOrganizations([])
      setNeedsOnboarding(false)
      apiClient.setTenantId(null)
      sessionStorage.removeItem('sb_current_org')
      window.location.href = '/login'
    } catch (error) {
      // Don't throw on sign out errors - user should be signed out anyway
      handleError(error, 'Sign out completed with warnings.')
      setUser(null)
      setCurrentOrganization(null)
      setOrganizations([])
      setNeedsOnboarding(false)
      apiClient.setTenantId(null)
      sessionStorage.removeItem('sb_current_org')
      // Still navigate to login even on error
      window.location.href = '/login?logged_out=true'
    } finally {
      setIsLoading(false)
    }
  }, [handleError])

  const refreshSession = useCallback(async (): Promise<boolean> => {
    try {
      const success = await scalekitAuthService.refreshSession()
      if (success) {
        const currentUser = await scalekitAuthService.getCurrentUser()
        if (currentUser) {
          setUser(mapScalekitUserToAuthUser(currentUser))
        }
      }
      return success
    } catch (error) {
      handleError(error, 'Session refresh failed')
      return false
    }
  }, [handleError])

  const isAuthenticated = useMemo(() => {
    return user !== null
  }, [user])

  const handleSetCurrentOrganization = useCallback((org: Organization) => {
    setCurrentOrganization(org)
    apiClient.setTenantId(org.id)
    sessionStorage.setItem('sb_current_org', JSON.stringify(org))
    // Fetch onboarding progress for this org
    organizationService.getOnboardingProgress(org.id).then(setOnboardingProgress).catch(() => {})
  }, [])

  // Directly update all auth state after org creation.
  // Eliminates the need for a second API call (checkOnboardingStatus) which
  // can fail transiently and leave needsOnboarding=true, causing a redirect loop.
  const handleOrgCreated = useCallback((org: Organization) => {
    setCurrentOrganization(org)
    setOrganizations([org])
    setNeedsOnboarding(false)
    setIsCheckingOrgs(false)
    apiClient.setTenantId(org.id)
    sessionStorage.setItem('sb_current_org', JSON.stringify(org))
  }, [])

  // Task 106 — Supabase Realtime is now scoped to the `authenticated`
  // role + a per-operator JWT carrying `tenant_id`. Mint/refresh that
  // token whenever the active organization (== tenant) is known, and
  // clear it when it isn't. The Realtime hooks bail out until the token
  // is applied, so they never subscribe on the (powerless) anon key.
  const currentOrgId = currentOrganization?.id ?? null
  useEffect(() => {
    if (currentOrgId) {
      startRealtimeAuth(currentOrgId)
    } else {
      stopRealtimeAuth()
    }
  }, [currentOrgId])

  const value = useMemo(
    () => ({
      user,
      isLoading,
      isAuthenticated,
      error,
      // Organization state
      currentOrganization,
      organizations,
      needsOnboarding,
      isCheckingOrgs,
      // Onboarding progress
      onboardingProgress,
      // Passwordless state
      passwordlessState,
      // Methods
      signIn,
      signInWithEmail,
      verifyCode,
      signOut,
      refreshSession,
      clearError,
      clearPasswordlessState,
      checkOnboardingStatus,
      setCurrentOrganization: handleSetCurrentOrganization,
      onOrgCreated: handleOrgCreated,
    }),
    [
      user,
      isLoading,
      isAuthenticated,
      error,
      currentOrganization,
      organizations,
      needsOnboarding,
      isCheckingOrgs,
      onboardingProgress,
      passwordlessState,
      signIn,
      signInWithEmail,
      verifyCode,
      signOut,
      refreshSession,
      clearError,
      clearPasswordlessState,
      checkOnboardingStatus,
      handleSetCurrentOrganization,
      handleOrgCreated,
    ]
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
})

AuthProvider.displayName = 'AuthProvider'
